import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import RevQSidebar from './RevQSidebar';
import RevQTopbar from './RevQTopbar';
import AppChatbot from '../shared/AppChatbot';
import GuidedDemoModal from '../shared/GuidedDemoModal';
import { Toaster } from '../ui/sonner';
import { EnterpriseProvider } from '../../contexts/EnterpriseContext';
import { useAppMode } from '../../contexts/AppModeContext';

/* Scheduler context — shared copy across all layouts */
const DEMO_SCHED_PROMPTS = [
  "Fill today's open slots",
  'Show no-show risks',
  'Move Emily Davis to 2 PM',
  'Who can I add to the waitlist?',
];
const DEMO_SCHED_RESP = (msg) => {
  const lower = msg.toLowerCase();
  if (lower.includes('fill') || lower.includes('open'))
    return "**1 open slot today** (11:30 AM with Dr. Chen).\n\nTop 3 waitlist matches:\n- Sarah Chen (Follow-up) — 88%\n- Mark Lee (Wellness) — 74%\n- Priya Patel (Sick Visit) — 69%\n\nWant me to send offers?";
  if (lower.includes('no-show') || lower.includes('risk'))
    return "**2 high-risk patients today:**\n- David Wilson (2 PM) — **42% risk**\n- James Taylor (11 AM) — **35% risk**\n\nReminders sent. Add a call nudge?";
  return "I'm your Scheduling agent. Try:\n- *Fill today's open slots*\n- *Show no-show risks*\n- *Move Emily Davis to 2 PM*";
};

const DEMO_REVQ_RESPONSE = (msg) => {
  const lower = msg.toLowerCase();
  if (lower.includes('claim') && (lower.includes('status') || lower.includes('track')))
    return "**Claim Status Summary:**\n\n- **Submitted:** 42 claims ($128,500)\n- **In Review:** 18 claims ($52,300)\n- **Paid:** 156 claims ($412,800)\n- **Denied:** 8 claims ($24,600)\n\nClean claim rate this month: **98.2%**. Would you like to see the denied claims?";
  if (lower.includes('denial') || lower.includes('denied') || lower.includes('appeal'))
    return "**Top Denial Reasons (This Month):**\n\n1. **Missing Modifier** — 3 claims ($8,200)\n2. **Auth Required** — 2 claims ($6,400)\n3. **Timely Filing** — 1 claim ($3,500)\n4. **Medical Necessity** — 2 claims ($6,500)\n\nTotal denied: **$24,600** (3.8% of submissions)\nAppeal success rate: **72%**";
  if (lower.includes('revenue') || lower.includes('collection'))
    return "**Revenue Snapshot:**\n\n- MTD Charges: **$687,520**\n- Net Collections: **$6,892,680** YTD\n- Collection Rate: **87.9%**\n- Avg Days in AR: **28 days**\n\nRevenue is trending **+4.2%** vs. last quarter.";
  if (lower.includes('ar') || lower.includes('aging') || lower.includes('accounts receivable'))
    return "**AR Aging Breakdown:**\n\n- 0-30 days: **$312,500** (57.6%)\n- 31-60 days: **$125,800** (23.2%)\n- 61-90 days: **$68,650** (12.7%)\n- 90+ days: **$35,500** (6.5%)\n\nTotal AR: **$542,450**\nGoal: Keep 90+ days below 5%.";
  if (lower.includes('payer') || lower.includes('insurance'))
    return "**Top Payers by Volume:**\n\n1. **Blue Cross** — 45 claims, 92% acceptance\n2. **Aetna** — 28 claims, 88% acceptance\n3. **UnitedHealth** — 35 claims, 91% acceptance\n4. **Cigna** — 22 claims, 85% acceptance\n\nBCBS has the fastest payment at avg **14 days**.";
  if (lower.includes('credit'))
    return "**Credit Balance: 412 credits**\n\n- AI Claim Generation: **3 credits** per claim\n- AI Denial Appeal: **2 credits** per appeal\n- AI Coding Review: **1 credit** per review\n\nYou can purchase more credits from the topbar.";
  return "I can help with:\n- **Claim status** and tracking\n- **Denial analysis** and appeal tips\n- **Revenue metrics** and trends\n- **AR aging** breakdown\n- **Payer performance** insights\n- **Credit usage** info\n\nWhat would you like to know?";
};

const DEMO_REVQ_PROMPTS = [
  'What is my claim status?',
  'Show denial breakdown',
  'Revenue snapshot',
  'AR aging summary',
];

const RevQLayout = () => {
  const { isDemo } = useAppMode();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const location = useLocation();
  const onScheduler = location.pathname.startsWith('/revq/scheduler');

  return (
    <EnterpriseProvider>
    <div className="min-h-screen bg-slate-50" data-testid="revq-layout">
      <RevQTopbar sidebarCollapsed={sidebarCollapsed} onStartTour={() => setShowDemo(true)} />
      <RevQSidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      
      <main className={`pt-14 min-h-screen transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        <div className="p-6 pb-20">
          <Outlet />
        </div>
      </main>

      <AppChatbot
        appName={onScheduler ? 'Echo Scheduling' : 'revQ'}
        subtitle={onScheduler ? 'Ask me to fill slots, reschedule, or check no-show risks.' : 'Ask me about claims, denials, revenue, or billing metrics.'}
        getResponse={!isDemo
          ? () => "I'm currently a demo-only assistant. Live revenue insights will be available once the chatbot service is connected."
          : (onScheduler ? DEMO_SCHED_RESP : DEMO_REVQ_RESPONSE)}
        suggestedPrompts={!isDemo
          ? ['How do I review denials?', 'Where are my claims?', 'What does Auto-Repair do?']
          : (onScheduler ? DEMO_SCHED_PROMPTS : DEMO_REVQ_PROMPTS)}
        tooltipText={onScheduler ? "I'm your Scheduling agent" : null}
      />
      <GuidedDemoModal app="revq" open={showDemo} onOpenChange={setShowDemo} />
      <Toaster position="top-right" />
    </div>
    </EnterpriseProvider>
  );
};

export default RevQLayout;
